import React, { useState, useEffect } from "react";

import {
  DataGridPro,
  gridClasses,
  GridColDef,
  GridRenderCellParams,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
} from "@mui/x-data-grid-pro"; // Changed to Pro package
import Box from "@mui/material/Box";
import {
  Tabs,
  Tab,
  styled,
  alpha,
  Radio,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Tooltip,
  Button,
} from "@mui/material";
import { useAuth } from "./AuthContext";
import {
  hitter_columns,
  hitter_columns_mobile,
  pitcher_columns,
  pitcher_columns_mobile,
} from "./utils/ColumnDefs";
import { useHeaderFilters } from "./utils/useHeaderFilters";

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGridPro)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      "&:hover": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
          theme.palette.action.selectedOpacity +
          theme.palette.action.hoverOpacity
        ),
        // Reset on touch devices, it doesn't add specificity
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: "white",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      "&:hover": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
          theme.palette.action.selectedOpacity +
          theme.palette.action.hoverOpacity
        ),
        // Reset on touch devices, it doesn't add specificity
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
}))

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  isDesktop?: boolean;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, isDesktop, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, ...(isDesktop ? {} : { px: 0 }) }}>{children}</Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}




export default function Leaderboard(props: any) {
  const apiRef = useGridApiRef();
  const { user } = useAuth();

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [hitterData, setHitterData] = useState([]);
  const [pitcherData, setPitcherData] = useState([]);
  const [loading, setLoading] = useState(true);

  const base_h_columns = props.isDesktop
    ? hitter_columns
    : hitter_columns_mobile;
  const base_p_columns = props.isDesktop
    ? pitcher_columns
    : pitcher_columns_mobile;

  const [level, setLevel] = React.useState("AAA");
  const [season, setSeason] = React.useState("2026");

  // Columns AA / High-A have no data for (no Statcast tracking) plus the
  // composite percentile columns (Agg / Power / Discipline), removed for the
  // non-Statcast aggregate levels.
  const AA_HIDDEN_HITTER = new Set([
    // percentile (heat-map) columns
    "xwoba_p", "xba_p", "xslg_p", "barrelbbe_p",
    "ev_p", "maxev_p", "ev50_p", "ev90_p",
    "hhrate_p", "langle_p",
    // raw value columns
    "xwoba", "xba", "xslg", "barrelbbe",
    "ev", "maxev", "ev50", "ev90",
    "hhrate", "langle", "barrelpa", "barrels",
    // composites
    "p_agg", "power_agg", "d_agg",
  ]);
  const AA_HIDDEN_PITCHER = new Set([
    // percentile (heat-map) columns
    "xwoba_p", "xba_p", "xslg_p", "barrelbbe_p",
    "ev_p", "hhrate_p", "langle_p", "velo_p", "swing_p",
    "release_extension_p",
    // raw value columns
    "xwoba", "xba", "xslg", "barrelbbe",
    "ev", "hhrate", "langle", "release_extension", "max_velo",
    // composites
    "p_agg", "power_agg", "d_agg",
  ]);

  // High-A is an aggregate (non-Statcast) level just like AA, but it DOES have
  // pitch-location tracking, so the zone-relative discipline metrics
  // (Chase / Z-Swing / Z-Contact) are kept.

  // AA and High-A are aggregate (non-Statcast) levels
  const isAggLevel = level === "AA" || level === "A+";

  // wOBACON is only computed for the aggregate (non-Statcast) levels.
  const AGG_ONLY_HITTER = new Set(["wobacon_p", "wobacon", "gbrate_p", "gbrate"]);
  // GB% is only computed for the aggregate (non-Statcast) levels for pitchers.
  const AGG_ONLY_PITCHER = new Set(["gbrate_p", "gbrate"]);

  // Single-A ("A") is a UNIFIED, hybrid level: the Florida State League has full
  // Statcast tracking, while the California / Carolina leagues are aggregate
  // (FanGraphs + GameDay-stringer zone discipline + GB% / wOBACON / PullAir) but
  // have no Statcast. Both tiers share one grid; per row we blank the columns the
  // row's tier can't fill, keyed off the explicit `aball_src` flag ("fsl"|"agg").
  const isABall = level === "A";
  // Only FSL fills these (Statcast) -> blank for the aggregate Cal/Carolina rows.
  // Zone discipline (chase/zswing/zcontact/zonerate) is NOT here: both tiers have
  // it (FSL via Statcast, Cal/Carolina via the stringer-pixel crawl).
  const ABALL_FSL_ONLY_HITTER = new Set<string>(Array.from(AA_HIDDEN_HITTER));
  const ABALL_FSL_ONLY_PITCHER = new Set<string>(Array.from(AA_HIDDEN_PITCHER));
  // Only the aggregate leagues compute these -> blank for FSL rows.
  const ABALL_AGG_ONLY_HITTER = new Set<string>(["wobacon_p", "wobacon", "gbrate_p", "gbrate"]);
  const ABALL_AGG_ONLY_PITCHER = new Set<string>(["gbrate_p", "gbrate"]);

  const wrapABall = (
    cols: GridColDef<any>[],
    fslOnly: Set<string>,
    aggOnly: Set<string>,
  ): GridColDef<any>[] =>
    cols.map((c) => {
      const f = c.field as string;
      const blankForAgg = fslOnly.has(f);
      const blankForFsl = aggOnly.has(f);
      if (!blankForAgg && !blankForFsl) return c;
      const orig = c.renderCell;
      return {
        ...c,
        renderCell: (params: GridRenderCellParams) => {
          const src = params.row.aball_src;
          if ((blankForAgg && src === "agg") || (blankForFsl && src === "fsl")) {
            return <span style={{ color: "#BBBBBB" }}>-</span>;
          }
          return orig ? orig(params) : params.value;
        },
      };
    });

  // Rookie ("Rk") is a UNIFIED, hybrid level: the Complex leagues (ACL / FCL)
  // carry PARTIAL Statcast harvested from the StatsAPI game-feed crawl, while the
  // Dominican Summer League (DSL) has none. Expected stats (xwOBA / xBA / xSLG)
  // are uncomputable at Rookie level for BOTH tiers, so they are hidden entirely.
  // Real-EV Statcast (EV / maxEV / barrels / HardHit / LA) is kept but blanked
  // per row for DSL, keyed off the explicit `rk_src` flag ("cpx"|"dsl").
  const isRookie = level === "Rk";
  const ROOKIE_HIDDEN_HITTER = new Set<string>([
    "xwoba_p", "xba_p", "xslg_p",
    "xwoba", "xba", "xslg",
    "p_agg", "power_agg", "d_agg",
  ]);
  const ROOKIE_HIDDEN_PITCHER = new Set<string>([
    "xwoba_p", "xba_p", "xslg_p",
    "xwoba", "xba", "xslg",
    "p_agg", "power_agg", "d_agg",
  ]);
  // Statcast columns only the Complex tier fills -> blank for DSL rows.
  const ROOKIE_DSL_ONLY_HITTER = new Set<string>([
    "ev_p", "maxev_p", "ev50_p", "ev90_p", "hhrate_p", "langle_p", "barrelbbe_p",
    "ev", "maxev", "ev50", "ev90", "hhrate", "langle", "barrelpa", "barrels", "barrelbbe",
  ]);
  const ROOKIE_DSL_ONLY_PITCHER = new Set<string>([
    "velo_p", "ev_p", "hhrate_p", "langle_p", "barrelbbe_p",
    "max_velo", "ev", "hhrate", "langle", "barrelbbe",
  ]);

  const wrapRookie = (
    cols: GridColDef<any>[],
    hidden: Set<string>,
    dslOnly: Set<string>,
  ): GridColDef<any>[] =>
    cols
      .filter((c) => !hidden.has(c.field as string))
      .map((c) => {
        const f = c.field as string;
        if (!dslOnly.has(f)) return c;
        const orig = c.renderCell;
        return {
          ...c,
          renderCell: (params: GridRenderCellParams) => {
            if (params.row.rk_src === "dsl") {
              return <span style={{ color: "#BBBBBB" }}>-</span>;
            }
            return orig ? orig(params) : params.value;
          },
        };
      });

  const h_columns = isAggLevel
    ? base_h_columns.filter((c) => !AA_HIDDEN_HITTER.has(c.field))
    : isABall
      ? wrapABall(base_h_columns, ABALL_FSL_ONLY_HITTER, ABALL_AGG_ONLY_HITTER)
      : isRookie
        ? wrapRookie(base_h_columns, ROOKIE_HIDDEN_HITTER, ROOKIE_DSL_ONLY_HITTER)
        : base_h_columns.filter((c) => !AGG_ONLY_HITTER.has(c.field));
  const p_columns = isAggLevel
    ? base_p_columns.filter((c) => !AA_HIDDEN_PITCHER.has(c.field))
    : isABall
      ? wrapABall(base_p_columns, ABALL_FSL_ONLY_PITCHER, ABALL_AGG_ONLY_PITCHER)
      : isRookie
        ? wrapRookie(base_p_columns, ROOKIE_HIDDEN_PITCHER, ROOKIE_DSL_ONLY_PITCHER)
        : base_p_columns.filter((c) => !AGG_ONLY_PITCHER.has(c.field));



  const handleLevel = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLevel(event.target.value);
  };
  //console.log(level);

  const handleSeason = (event: SelectChangeEvent) => {
    setSeason(event.target.value as string);
  };
  //console.log(season);
  // Sample-size floor: plate appearances for hitters, innings for pitchers.
  const [paQualifier, setPaQualifier] = React.useState("100");
  const [ipQualifier, setIpQualifier] = React.useState("30");

  const isPitcherTab = value === 1;
  const qualifier = isPitcherTab ? ipQualifier : paQualifier;

  const handleQualifier = (event: SelectChangeEvent) => {
    (isPitcherTab ? setIpQualifier : setPaQualifier)(event.target.value as string);
  };

  const [organization, setOrganization] = React.useState("All");

  const handleOrganization = (event: SelectChangeEvent) => {
    setOrganization(event.target.value as string);
  };


  const [ageRange, setAgeRange] = React.useState<number[]>([16, 28]);

  const handleAgeRange = (event: Event, newValue: any) => {
    setAgeRange(newValue);
  };

  // Mobile-only zoom factor, adjustable via the +/- toolbar buttons.
  const [mobileScale, setMobileScale] = React.useState(0.7);

  const { headerFilters, headerFilterButton } = useHeaderFilters();

  const decreaseScale = () =>
    setMobileScale((s) => Math.max(0.4, Math.round((s - 0.1) * 10) / 10));
  const increaseScale = () =>
    setMobileScale((s) => Math.min(1, Math.round((s + 0.1) * 10) / 10));

  // Mimic a browser zoom on mobile: scale the grid down and grow its layout
  // size so the scaled result fills the container. The parent Box clips the
  // overflow so no empty space remains.
  const scaledGridSx = props.isDesktop
    ? { height: "80vh", width: "100%" }
    : {
        height: `${80 / mobileScale}vh`,
        width: `${100 / mobileScale}%`,
        transform: `scale(${mobileScale})`,
        transformOrigin: "top left",
      };

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ backgroundColor: "#FFFFFF" }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        {headerFilterButton}
        <GridToolbarDensitySelector />
        {(user?.is_plus || /^(localhost|127\.0\.0\.1|192\.168\.|10\.)/.test(window.location.hostname)) ? (
          <GridToolbarExport />
        ) : (
          <Tooltip title="PS Plus required to export">
            <span>
              <Button
                size="small"
                href="/plus"
                sx={{ fontSize: "0.8125rem", textTransform: "none", color: "rgba(0,0,0,0.38)" }}
              >
                Export 🔒
              </Button>
            </span>
          </Tooltip>
        )}
        {!props.isDesktop && (
          <>
            <Tooltip title="Zoom out">
              <Button size="small" onClick={decreaseScale} sx={{ minWidth: 32 }}>
                −
              </Button>
            </Tooltip>
            <Tooltip title="Zoom in">
              <Button size="small" onClick={increaseScale} sx={{ minWidth: 32 }}>
                +
              </Button>
            </Tooltip>
          </>
        )}
        {/* Added Quick Filter */}
        <GridToolbarQuickFilter
          quickFilterParser={(searchInput) =>
            searchInput.split(',').map((value) => value.trim())
          }
          sx={{ marginLeft: 'auto', width: props.isDesktop ? 500 : 250 }}
        />
        {level === "A" && (
          <div>
            <span style={{ fontWeight: "bold", color: "#DC707A" }}>
              ** Single-A combines the Florida State League (full Statcast tracking) with the California &amp; Carolina leagues (no Statcast, but plate-discipline and batted-ball rates from pitch tracking). Blank cells mean that metric isn't tracked for that player's league. **
            </span>
          </div>
        )}
        {level === "Rk" && (
          <div>
            <span style={{ fontWeight: "bold", color: "#DC707A" }}>
              ** Rookie ball combines the Complex leagues (ACL &amp; FCL, with PARTIAL Statcast harvested from game tracking) and the Dominican Summer League (no Statcast). Exit-velocity and barrel metrics show only for tracked Complex games; expected stats aren't available at this level. Blank cells mean that metric isn't tracked for that player. **
            </span>
          </div>
        )}
        {(level === "AA" || level === "A+") && (
          <div>
            <span style={{ fontWeight: "bold", color: "#DC707A" }}>
              ** {level === "AA" ? "Double-A" : "High-A"} has no Statcast tracking: exit velocity, expected stats, and barrels are unavailable. Plate-discipline and batted-ball rates are derived from pitch tracking. **
            </span>
          </div>
        )}
      </GridToolbarContainer>
    );
  }


  useEffect(() => {
    //console.log("Data: ", level);
    let cancelled = false;
    setLoading(true);

    const hitterFetch = fetch(props.prod ? "https://oriolebird.pythonanywhere.com/leaders/hitters/" + level + "/" + season + "/" + paQualifier + "/" + ageRange[0] + "/" + ageRange[1] : "/leaders/hitters/" + level + "/" + season + "/" + paQualifier + "/" + ageRange[0] + "/" + ageRange[1])
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        //console.log(data);
        setHitterData(data.data);
        //console.log("HDATA", data.data);
      });
    const pitcherFetch = fetch(props.prod ? "https://oriolebird.pythonanywhere.com/leaders/pitchers/" + level + "/" + season + "/" + ipQualifier + "/" + ageRange[0] + "/" + ageRange[1] : "/leaders/pitchers/" + level + "/" + season + "/" + ipQualifier + "/" + ageRange[0] + "/" + ageRange[1])
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        //console.log(data);
        setPitcherData(data.data);
        //console.log("PDATA", data.data);
      });

    Promise.all([
      hitterFetch.catch(() => {}),
      pitcherFetch.catch(() => {}),
    ]).then(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [level, props.prod, season, paQualifier, ipQualifier, ageRange]);

  return (
    <div style={{ width: "100%" }}>
      <title>Prospect Savant</title>
      <meta
        name="description"
        content="Prospect Savant - Minor League Baseball Savant"
      />
      <meta
        name="title"
        content="Prospect Savant"
      />
      <meta property="og:description" content="Prospect Savant - Minor League Baseball Savant" />
      <meta property="og:title" content="Prospect Savant" />
      <Box sx={{ width: "100%", marginTop: "-15px" }}>
        <Box
          sx={{ borderBottom: 1, borderColor: "divider" }}
          paddingLeft={"50px"}
          flexDirection="row"
        >
          <Tabs value={value} onChange={handleChange}>
            <Tab label="Hitters" {...a11yProps(0)} />
            <Tab label="Pitchers" {...a11yProps(1)} />
          </Tabs>
        </Box>
        <div
          style={{
            marginTop: "10px",
            marginBottom: "-20px",
            marginLeft: "30px",
            verticalAlign: "baseline",
          }}
        >
          <FormControl>
            <FormLabel id="demo-row-radio-buttons-group-label">Level</FormLabel>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
            >
              <FormControlLabel
                value="Rk"
                control={
                  <Radio
                    checked={level === "Rk"}
                    onChange={handleLevel}
                    value="Rk"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "Rk" }}
                  />
                }
                label="Rk"
              />
              <FormControlLabel
                value="A"
                control={
                  <Radio
                    checked={level === "A"}
                    onChange={handleLevel}
                    value="A"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "A" }}
                  />
                }
                label="A"
              />
              <FormControlLabel
                value="A+"
                control={
                  <Radio
                    checked={level === "A+"}
                    onChange={handleLevel}
                    value="A+"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "A+" }}
                  />
                }
                label="A+"
              />
              <FormControlLabel
                value="AA"
                control={
                  <Radio
                    checked={level === "AA"}
                    onChange={handleLevel}
                    value="AA"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "AA" }}
                  />
                }
                label="AA"
              />
              <FormControlLabel
                value="AAA"
                control={
                  <Radio
                    checked={level === "AAA"}
                    onChange={handleLevel}
                    value="AAA"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "AAA" }}
                  />
                }
                label="AAA"
              />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <InputLabel id="demo-simple-select-label">Season</InputLabel>
            <Select
              value={season}
              label="Season"
              onChange={handleSeason}
              defaultValue="2026"
            >
              <MenuItem value={2026}>2026</MenuItem>
              <MenuItem value={2025}>2025</MenuItem>
              <MenuItem value={2024}>2024</MenuItem>
              <MenuItem value={2023}>2023</MenuItem>
            </Select>
          </FormControl>
          <FormControl style={{ marginLeft: "10px" }}>
            <InputLabel id="demo-simple-select-label">
              {isPitcherTab ? "Min IP" : "Min PA"}
            </InputLabel>
            <Select
              value={qualifier}
              label={isPitcherTab ? "Min IP" : "Min PA"}
              onChange={handleQualifier}
              sx={{ width: "102px" }}
            >
              {(isPitcherTab
                ? [1, 5, 10, 20, 30, 40, 50, 60, 80, 100, 120, 150]
                : [1, 10, 25, 50, 75, 100, 150, 200, 250, 300, 350, 400, 450, 500]
              ).map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl style={{ marginLeft: "10px" }}>
            <InputLabel id="demo-simple-select-label">Organization</InputLabel>
            <Select
              value={organization}
              label="Organization"
              onChange={handleOrganization}
              defaultValue="All"
              sx={{ width: "100px", }}
            >
              <MenuItem value={"All"}>All</MenuItem>
              <MenuItem value={"ARI"}>ARI</MenuItem>
              <MenuItem value={"ATL"}>ATL</MenuItem>
              <MenuItem value={"ATH"}>ATH</MenuItem>
              <MenuItem value={"BAL"}>BAL</MenuItem>
              <MenuItem value={"BOS"}>BOS</MenuItem>
              <MenuItem value={"CHC"}>CHC</MenuItem>
              <MenuItem value={"CHW"}>CHW</MenuItem>
              <MenuItem value={"CIN"}>CIN</MenuItem>
              <MenuItem value={"CLE"}>CLE</MenuItem>
              <MenuItem value={"COL"}>COL</MenuItem>
              <MenuItem value={"DET"}>DET</MenuItem>
              <MenuItem value={"HOU"}>HOU</MenuItem>
              <MenuItem value={"KCR"}>KCR</MenuItem>
              <MenuItem value={"LAA"}>LAA</MenuItem>
              <MenuItem value={"LAD"}>LAD</MenuItem>
              <MenuItem value={"MIA"}>MIA</MenuItem>
              <MenuItem value={"MIL"}>MIL</MenuItem>
              <MenuItem value={"MIN"}>MIN</MenuItem>
              <MenuItem value={"NYM"}>NYM</MenuItem>
              <MenuItem value={"NYY"}>NYY</MenuItem>
              <MenuItem value={"PHI"}>PHI</MenuItem>
              <MenuItem value={"PIT"}>PIT</MenuItem>
              <MenuItem value={"SDP"}>SDP</MenuItem>
              <MenuItem value={"SEA"}>SEA</MenuItem>
              <MenuItem value={"SFG"}>SFG</MenuItem>
              <MenuItem value={"STL"}>STL</MenuItem>
              <MenuItem value={"TBR"}>TBR</MenuItem>
              <MenuItem value={"TEX"}>TEX</MenuItem>
              <MenuItem value={"TOR"}>TOR</MenuItem>
              <MenuItem value={"WSN"}>WSN</MenuItem>
            </Select>
          </FormControl>
          {!props.isDesktop && <br></br>}
          <span style={{ position: "absolute", marginLeft: props.isDesktop ? "25px" : "0px", marginTop: "3px", color: "#5A6465", fontSize: 16 }}>Age Range</span>
          <Slider
            getAriaLabel={() => 'Age Range'}
            value={ageRange}
            onChange={handleAgeRange}
            valueLabelDisplay="on"
            min={16}
            max={40}
            disableSwap={true}
            sx={{
              width: "200px", marginLeft: props.isDesktop ? "30px" : "10px", marginTop: "25px",
              '& .MuiSlider-valueLabel': {
                fontSize: 11,
                fontWeight: 'bold',
                marginTop: "33px",
                backgroundColor: 'unset',
                color: '#FFFFFF',
                '&::before': {
                  display: 'none',
                },
                '& *': {
                  background: 'transparent',
                  color: '#FFFFFF',
                },
              },
            }}
          />
        </div>
        <CustomTabPanel value={value} index={0} isDesktop={props.isDesktop}>
          <Box sx={props.isDesktop ? {} : { height: "80vh", overflow: "hidden" }}>
          <StripedDataGrid
            apiRef={apiRef} // Added for Pro API access
            loading={loading}
            rows={organization === "All" ? hitterData : hitterData.filter((row: any) => row.MLB_AbbName === organization)}
            sx={scaledGridSx}
            columns={h_columns}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            pageSizeOptions={[25, 50, 100]}

            disableColumnResize={false} // Enable column resizing
            headerFilters={headerFilters}

            slots={{
              toolbar: CustomToolbar,
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: "score_p", sort: "desc" }],
              },
              density: "compact",
              pinnedColumns: {
                left: ['name'], // Pin player name by default
              }, // Initial pinned columns
            }}
          />
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1} isDesktop={props.isDesktop}>
          <Box sx={props.isDesktop ? {} : { height: "80vh", overflow: "hidden" }}>
          <StripedDataGrid
            apiRef={apiRef} // Added for Pro API access
            loading={loading}
            sx={scaledGridSx}
            rows={organization === "All" ? pitcherData : pitcherData.filter((row: any) => row.MLB_AbbName === organization)}
            columns={p_columns}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            pagination
            pageSizeOptions={[25, 50, 100]}

            disableColumnResize={false} // Enable column resizing
            headerFilters={headerFilters}

            slots={{
              toolbar: CustomToolbar,
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: "score_p", sort: "desc" }],
              },
              density: "compact",
              pinnedColumns: {
                left: ['name'], // Pin player name by default
              }, // Initial pinned columns
            }}
          />
          </Box>
        </CustomTabPanel>
      </Box>
    </div>
  );
}
